import React, { useEffect, useState, FC } from 'react';
import { ContentHubSeriesListingFiltersProps } from './types/seriesListing.types';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import { SeriesListingModel } from './models/seriesListing.model';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import { ComponentRendering, isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { documentService } from '../../utils/window.utils';
import { withTranslation, WithTranslation } from 'react-i18next';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import FirstFilter from './components/firstFilter';
import SecondFilter from './components/secondFilter';

const ProductSeriesListingFilters: FC<WithTranslation & ContentHubSeriesListingFiltersProps> = (props) => {
  const apiResponse = isEmptyResponse(props);
  const { t } = props;
  let model = new SeriesListingModel(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);

  const ALLTYPES = t('cnhi-allTypes');
  const OTHER = t('cnhi-other');

  const isMobile = useMediaQuery(`(max-width: ${BreakpointsInPx.md})`);

  const [showDropdown, setShowDropdown] = useState(false);
  const [showDropdownType, setShowDropdownType] = useState(false);
  const [dropdownTypeText, setDropdownTypeText] = useState(ALLTYPES);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (documentService()) {
      const matches = documentService().getElementsByClassName('product-card__wrapper');
      for (const match of matches) {
        match.classList.remove('hidden');
      }
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    model = new SeriesListingModel(props); // eslint-disable-line
  }, [props]);

  const handleClickOutside = (event, ref, refDropdown, cb) => {
    if (ref.current && !ref.current.contains(event?.target) && refDropdown.current && !refDropdown.current.contains(event?.target)) {
      cb(false);
    }
  };

  useEffect(() => {
    if (showPopup) {
      documentService()?.body?.classList?.add('body--with-popup-open');
    } else {
      documentService()?.body?.classList?.remove('body--with-popup-open');
    }
  }, [showPopup]);

  const applyFilter = (tractor) => {
    if (documentService()) {
      const cards = documentService().querySelectorAll('.product-card__wrapper');
      cards?.forEach((el) => el.classList.add('hidden'));
      if (tractor === ALLTYPES) {
        cards?.forEach((el) => el.classList.remove('hidden'));
      }

      if (tractor === OTHER) {
        const matches: Array<HTMLElement> = [];
        cards.forEach((el) => {
          if (el.querySelector('.product-card__eyebrow')) return;
          matches.push(el);
        });
        matches.forEach((el) => el?.classList.remove('hidden'));
      }

      if (tractor !== ALLTYPES && tractor !== OTHER) {
        const matches = documentService().getElementsByClassName(encodeURI(tractor));
        for (const match of matches) {
          match.classList.remove('hidden');
        }
      }
    }

    setDropdownTypeText(tractor);
    setShowDropdownType(false);
  };

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const iconType = isMobile ? 'action-close' : 'arrow-chevron-up';

  return (
    <AnalyticsVisibleEvent rendering={props?.rendering as ComponentRendering} title="Filter" template="Series Filter">
      <ul className="product-card-filters__wrapper">
        <FirstFilter
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          showPopup={showPopup}
          setShowPopup={setShowPopup}
          setDropdownTypeText={setDropdownTypeText}
          applyFilter={applyFilter}
          handleClickOutside={handleClickOutside}
          isMobile={isMobile}
          currentLineTitle={model.currentLineTitle}
          isSuperSerie={model.isSuperSerie}
          lines={model.lines}
          linesSeries={model.linesSeries}
          ALLTYPES={ALLTYPES}
          iconType={iconType}
        />
        <SecondFilter
          showPopup={showPopup}
          setShowPopup={setShowPopup}
          dropdownTypeText={dropdownTypeText}
          showDropdownType={showDropdownType}
          setShowDropdownType={setShowDropdownType}
          applyFilter={applyFilter}
          handleClickOutside={handleClickOutside}
          isMobile={isMobile}
          iconType={iconType}
          ALLTYPES={ALLTYPES}
          OTHER={OTHER}
          series={model.series}
        />
      </ul>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(withTranslation()(ProductSeriesListingFilters));
